import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiUrls } from 'projects/bis-shared/src/lib/utility/apiurls-shared';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { HealthcareProviderLicense } from '../../healthcare-provider/healthcare-provider-license';
import { Group } from 'projects/bis-shared/src/lib/shared-models/group';
import {
  EntityTypeCode,
  ObjectSubTypeEnum,
} from 'projects/bis-shared/src/lib/utility/system-type-constants';
import { ISelectOptionGroup } from 'projects/bis-shared/src/lib/dynamic-forms/models/select-option-group';
import { SelectHelpers } from 'projects/bis-shared/src/lib/utility/select-helpers';
import { PriorAuthorizationBase } from './models/prior-authorization-base.model';
import { HealthcareProvider } from 'projects/bis-shared/src/lib/entities/healthcare-provider';
import { ClaimSearch } from './models/claim-search.model';
import { PriorAuthorizationRequestType } from 'projects/bis-shared/src/lib/utility/ref-constants';
import { Mtg } from './models/mtg.model';
import { IdentityService } from 'projects/bis-shared/src/lib/auth/identity.service';
import { ExternalApiUrls } from 'projects/bis-shared/src/lib/utility/apiurls-external';
import {
  EFormAttachment,
  ValidateAttachmentsResponse,
} from 'projects/bis-shared/src/lib/webforms/web-form-attachments/web-form-attachment.models';
import { WorkRequestConfig } from 'projects/bis-shared/src/lib/work-request-shared/work-request-config';
import { ObjectStatusConstants } from 'projects/bis-shared/src/lib/work-request-shared/object-status-constants';
import { WillHandleErrorMessagingOption } from 'projects/bis-shared/src/lib/utility/http-options';

@Injectable({
  providedIn: 'root',
})
export class PriorAuthRequestService {
  private http = inject(HttpClient);
  private apiUrls = inject(ApiUrls);
  private externalApiUrls = inject(ExternalApiUrls);
  private identityService = inject(IdentityService);

  

  readonly BODY_PART_FINGER_CODE = 'Finger(s)';
  readonly BODY_PART_TOE_CODE = 'Toes';

  hcpcsCodesCache: ISelectOptionGroup[];
  cptCodesCache: ISelectOptionGroup[];
  onBehalfOf: string;

  providerCanSubmitParCache = new Object();

  getNewPriorAuthorizationRequest(): PriorAuthorizationBase {
    return {
      lineItems: [],
      claimSearchRequest: {},
      filingTypeId: 1,
      uploadedAttachmentsPath: uuidv4(),
    };
  }

  getParShortCode(parTypeId: number) {
    switch (Math.floor(parTypeId)) {
      case PriorAuthorizationRequestType.Confirmation:
        return 'MG-1';
      case PriorAuthorizationRequestType.Medication:
        return 'RX';
      case PriorAuthorizationRequestType.DurableMedicalEquipment:
        return 'DME';
      case PriorAuthorizationRequestType.NonMtgBodyPartOver1000:
        return 'O1K';
      case PriorAuthorizationRequestType.NonMtgBodyPartUnder1000:
        return 'U1K';
      case PriorAuthorizationRequestType.SpecialServices:
        return 'SS';
      case PriorAuthorizationRequestType.Variance:
        return 'MG-2';
      default:
        return '';
    }
  }

  getParTypeDisplay(parTypeId: number) {
    switch (Math.floor(parTypeId)) {
      case PriorAuthorizationRequestType.Confirmation:
        return 'MTG Confirmation';
      case PriorAuthorizationRequestType.Medication:
        return 'Medication';
      case PriorAuthorizationRequestType.DurableMedicalEquipment:
        return 'Durable Medical Equipment';
      case PriorAuthorizationRequestType.NonMtgBodyPartOver1000:
        return 'Non-MTG Over $1000';
      case PriorAuthorizationRequestType.NonMtgBodyPartUnder1000:
        return 'Non-MTG Under or =$1000';
      case PriorAuthorizationRequestType.SpecialServices:
        return 'MTG Special Services';
      case PriorAuthorizationRequestType.Variance:
        return 'MTG Variance';
      default:
        return '';
    }
  }
  getHCPCSNameByParType(parTypeId: number) {
    switch (Math.floor(parTypeId)) {
      case PriorAuthorizationRequestType.DurableMedicalEquipment:
        return 'HCPCS';
      default:
        return 'CDT/CPT';
    }
  }
  getSideOfBodyLabel = (bodyPartCode: string) => {
    switch (bodyPartCode) {
      case this.BODY_PART_FINGER_CODE:
        return 'Finger';
      case this.BODY_PART_TOE_CODE:
        return 'Toe';
      default:
        return 'Side of Body';
    }
  };

  getRelatedHealthcareProviders(): Observable<Group[]> {
    return this.identityService.getUserGroups([EntityTypeCode.HealthcareProvider]);
  }

  getHealthcareProvider(id: number): Observable<HealthcareProvider> {
    return this.http.get<HealthcareProvider>(this.externalApiUrls.healthcareProvider.get(id));
  }

  getHealthcareProviderLicenses(healthcareProviderId: number): Observable<ISelectOptionGroup[]> {
    return this.http
      .get<HealthcareProviderLicense[]>(
        this.externalApiUrls.healthcareProvider.licenses(healthcareProviderId),
      )
      .pipe(
        map((data: HealthcareProviderLicense[]) =>
          SelectHelpers.toSelectList(
            data,
            (license: HealthcareProviderLicense) =>
              `${license.providerLicenseNumber} ${license.healthcareProviderProfession}`,
            (license: HealthcareProviderLicense) => license.healthcareProviderLicenseId,
          ),
        ),
      );
  }

  getHealthcareProviderZipCode(healthcareProviderId: number): Observable<number> {
    return this.http.get<number>(
      this.externalApiUrls.healthcareProvider.getPrimaryZipCode(healthcareProviderId),
    );
  }

  getHealthcareProviderOutOfState(healthcareProviderId: number): Observable<boolean> {
    return this.http.get<boolean>(
      this.externalApiUrls.healthcareProvider.getOutOfState(healthcareProviderId),
    );
  }

  searchClaims(criteria: ClaimSearch): Observable<PriorAuthorizationBase> {
    return this.http.post<any>(
      this.apiUrls.claim.search(),
      criteria,
      WillHandleErrorMessagingOption,
    );
  }

  getClaimData(claimData) {
    let updatedClaimData = claimData;
    if (claimData.cancelIntoCaseId) {
      updatedClaimData = Object.assign({}, claimData, {
        wcbCaseNumber: claimData.cancelIntoCaseId,
        cancelFromCaseId: claimData.caseId,
      });
      delete updatedClaimData.cancelIntoCaseId;
    }
    return updatedClaimData;
  }

  saveParDraft(documentFormId: number, form: any) {
    return this.http.post<number>(
      this.externalApiUrls.filing.saveDraftEForm(documentFormId),
      form,
      WillHandleErrorMessagingOption,
    );
  }

  discardDraftEForm(documentFormId: number) {
    return this.http.delete(this.externalApiUrls.filing.discardDraftEForm(documentFormId));
  }

  getParDraft(filingId: number) {
    return this.http.get<any>(this.externalApiUrls.filing.getDraftEForm(filingId));
  }

  getParEForm(filingId: number) {
    return this.http.get<any>(this.externalApiUrls.filing.getEForm(filingId));
  }

  readytoSubmitPar(requestType: PriorAuthorizationRequestType, form: any): Observable<number> {
    switch (requestType) {
      case PriorAuthorizationRequestType.Confirmation:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.confirmation.readyToSubmit(),
          form,
        );
      case PriorAuthorizationRequestType.Medication:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.medication.readyToSubmit(),
          form,
        );
      case PriorAuthorizationRequestType.DurableMedicalEquipment:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.durableMedicalEquipment.readyToSubmit(),
          form,
        );
      case PriorAuthorizationRequestType.NonMtgBodyPartOver1000:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.nonMtgOver1000.readyToSubmit(),
          form,
        );
      case PriorAuthorizationRequestType.NonMtgBodyPartUnder1000:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.nonMtgUnder1000.readyToSubmit(),
          form,
        );
      case PriorAuthorizationRequestType.SpecialServices:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.specialServices.readyToSubmit(),
          form,
        );
      case PriorAuthorizationRequestType.Variance:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.variance.readyToSubmit(),
          form,
        );
      default:
        alert('Invalid Prior Auth Request Type');
        break;
    }
  }

  submitPar(requestType: PriorAuthorizationRequestType, form: any): Observable<number> {
    switch (requestType) {
      case PriorAuthorizationRequestType.Confirmation:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.confirmation.submit(),
          form,
        );
      case PriorAuthorizationRequestType.Medication:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.medication.submit(),
          form,
        );
      case PriorAuthorizationRequestType.DurableMedicalEquipment:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.durableMedicalEquipment.submit(),
          form,
        );
      case PriorAuthorizationRequestType.NonMtgBodyPartOver1000:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.nonMtgOver1000.submit(),
          form,
        );
      case PriorAuthorizationRequestType.NonMtgBodyPartUnder1000:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.nonMtgUnder1000.submit(),
          form,
        );
      case PriorAuthorizationRequestType.SpecialServices:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.specialServices.submit(),
          form,
        );
      case PriorAuthorizationRequestType.Variance:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.variance.submit(),
          form,
        );
      default:
        alert('Invalid Prior Auth Request Type');
        break;
    }
  }

  previewPar(requestType: PriorAuthorizationRequestType, form: any): Observable<number> {
    switch (requestType) {
      case PriorAuthorizationRequestType.Confirmation:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.confirmation.preview(),
          form,
        );
      case PriorAuthorizationRequestType.Medication:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.medication.preview(),
          form,
        );
      case PriorAuthorizationRequestType.DurableMedicalEquipment:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.durableMedicalEquipment.preview(),
          form,
        );
      case PriorAuthorizationRequestType.NonMtgBodyPartOver1000:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.nonMtgOver1000.preview(),
          form,
        );
      case PriorAuthorizationRequestType.NonMtgBodyPartUnder1000:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.nonMtgUnder1000.preview(),
          form,
        );
      case PriorAuthorizationRequestType.SpecialServices:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.specialServices.preview(),
          form,
        );
      case PriorAuthorizationRequestType.Variance:
        return this.http.post<number>(
          this.externalApiUrls.priorAuthorizationRequest.variance.preview(),
          form,
        );
      default:
        alert('Invalid Prior Auth Request Type');
        break;
    }
  }

  getDaysToRespond(priorAuthType: PriorAuthorizationRequestType) {
    const request = {
      objectSubTypeId: this.getObjectSubTypeFromPriorAuthorizationType(priorAuthType),
      objectStatusId: ObjectStatusConstants.PriorAuthorization.L1ReviewRequested,
    };
    return this.http.post<WorkRequestConfig>(
      this.externalApiUrls.workRequest.getConfiguration(),
      request,
    );
  }

  // PAR Type Logic

  checkForExistingSpecialServicePar(wcbCaseNumber: string, mtgReferenceId: number) {
    const request = {
      wcbCaseNumber,
      mtgReferenceId,
    };
    return this.http.post<boolean>(
      this.externalApiUrls.priorAuthorizationRequest.support.checkForExistingSpecialServicePar(),
      request,
    );
  }

  checkForExistingMtgPar(wcbCaseNumber: string, mtgReferenceId: number) {
    const request = {
      wcbCaseNumber,
      mtgReferenceId,
    };
    return this.http.post<boolean>(
      this.externalApiUrls.priorAuthorizationRequest.support.checkForExistingMtgPar(),
      request,
    );
  }

  // Reference Helpers

  getMtgSites() {
    return this.http
      .get<Mtg[]>(this.apiUrls.priorAuthorizationRequest.reference.getMtgSites())
      .pipe(
        map(mtgs =>
          SelectHelpers.toSelectList(
            mtgs,
            mtg => mtg.name,
            mtg => mtg.id,
          ),
        ),
      );
  }

  getMtgsByMtgSite(mtgSiteId: number) {
    return this.http
      .get<Mtg[]>(
        this.apiUrls.priorAuthorizationRequest.reference.getMtgsByMtgSite(mtgSiteId),
        WillHandleErrorMessagingOption,
      )
      .pipe(
        map(mtgs =>
          SelectHelpers.toSelectList(
            mtgs,
            mtg => `${mtg.code}: ${mtg.name}`,
            mtg => mtg.id,
          ),
        ),
      );
  }

  getBodyPartsByMtgSite(mtgSiteId: number) {
    return this.http
      .get<Mtg[]>(
        this.apiUrls.priorAuthorizationRequest.reference.getBodyPartsByMtgSite(mtgSiteId),
        WillHandleErrorMessagingOption,
      )
      .pipe(
        map(mtgs =>
          SelectHelpers.toSelectList(
            mtgs,
            mtg => mtg.name,
            mtg => mtg.id,
          ),
        ),
      );
  }

  getSidesOfBodyByBodyPart(bodyPartId: number) {
    return this.http
      .get<Mtg[]>(
        this.apiUrls.priorAuthorizationRequest.reference.getSidesOfBodyByBodyPart(bodyPartId),
        WillHandleErrorMessagingOption,
      )
      .pipe(
        map(sidesOfBody =>
          SelectHelpers.toSelectList(
            sidesOfBody,
            sideOfBody => sideOfBody.name,
            sideOfBody => sideOfBody.id,
          ),
        ),
      );
  }

  getMedicalFeeScheduleRegion(zipCode: string) {
    if (zipCode.endsWith('-')) {
      zipCode = zipCode.slice(0, -1);
    }
    return this.http.get<number>(
      this.apiUrls.priorAuthorizationRequest.reference.getMedicalFeeScheduleRegion(zipCode),
    );
  }

  getConversionFactor(
    providerProfessionId: any,
    medicalFeeScheduleSectionId: number,
    medicalFeeScheduleRegionId: number,
  ) {
    const request = {
      providerProfessionId,
      medicalFeeScheduleSectionId,
      medicalFeeScheduleRegionId,
    };
    return this.http.post<number>(
      this.apiUrls.priorAuthorizationRequest.reference.getConversionFactor(),
      request,
    );
  }

  checkHcpCanCreatePar(providerProfessionId: number, parRequestType?: number) {
    const cacheKey = `${providerProfessionId}-${parRequestType}`;
    const cacheValue = this.providerCanSubmitParCache[cacheKey];
    if (cacheValue != null) {
      return of(cacheValue);
    } else {
      const request = {
        providerProfessionId,
        parRequestType: parRequestType || null,
      };
      return this.http
        .post<boolean>(
          this.apiUrls.priorAuthorizationRequest.reference.checkHcpCanCreatePar(),
          request,
        )
        .pipe(
          tap(providerCanSubmit => (this.providerCanSubmitParCache[cacheKey] = providerCanSubmit)),
        );
    }
  }

  checkUserCanSubmitPar(
    healthcareProviderId: number,
    providerProfessionId: number,
    priorAuthorizationRequestTypeId: number,
  ) {
    return this.http.post<boolean>(
      this.externalApiUrls.priorAuthorization.checkUserCanSubmitPar(),
      {
        healthcareProviderId,
        providerProfessionId,
        priorAuthorizationRequestTypeId,
      },
    );
  }

  validateAttachments(documents: EFormAttachment[]): Observable<ValidateAttachmentsResponse> {
    return this.http.post<ValidateAttachmentsResponse>(
      this.externalApiUrls.document.validateAttachments(),
      { attachments: documents },
      WillHandleErrorMessagingOption,
    );
  }

  checkCptMtgSiteMapping(cptCodeId: number, mtgSiteId: number) {
    return this.http.post<boolean>(
      this.apiUrls.priorAuthorizationRequest.reference.checkCptMtgSiteMapping(),
      {
        cptCodeId,
        mtgSiteId,
      },
    );
  }

  GetMtgSiteByCptCode(cptCodeId: number) {
    return this.http.get<Mtg[]>(
      this.apiUrls.priorAuthorizationRequest.reference.GetMtgSiteByCptCode(cptCodeId),
    ) .pipe(
      map(mtgs =>
        SelectHelpers.toSelectList(
          mtgs,
          mtg => mtg.name,
          mtg => mtg.id,
        ),
      ),
    );
  }

  getFilingSubmissionStatus(filingSubmissionId: number): Observable<any> {
    return this.http.get<any>(
      this.externalApiUrls.filing.getFilingSubmissionStatus(filingSubmissionId),
    );
  }

  // Helper Methods

  getObjectSubTypeFromPriorAuthorizationType(priorAuthType: PriorAuthorizationRequestType) {
    switch (priorAuthType) {
      case PriorAuthorizationRequestType.Confirmation:
        return ObjectSubTypeEnum.ConfirmationPriorAuth;
      case PriorAuthorizationRequestType.DurableMedicalEquipment:
        return ObjectSubTypeEnum.DurableMedicalEquipmentPriorAuth;
      case PriorAuthorizationRequestType.Medication:
        return ObjectSubTypeEnum.MedicationPriorAuth;
      case PriorAuthorizationRequestType.NonMtgBodyPartOver1000:
        return ObjectSubTypeEnum.NonMtgOver1000PriorAuth;
      case PriorAuthorizationRequestType.NonMtgBodyPartUnder1000:
        return ObjectSubTypeEnum.NonMtgUnder1000PriorAuth;
      case PriorAuthorizationRequestType.SpecialServices:
        return ObjectSubTypeEnum.SpecialServicesPriorAuth;
      case PriorAuthorizationRequestType.Variance:
        return ObjectSubTypeEnum.VariancePriorAuth;
      default:
        return null;
    }
  }
}
